import { type TIdentityResponse } from 'features/auth/types';
import { mapMeToCurrentUser } from 'features/user/helpers';
import { baseApi } from 'lib/rtkQuery/baseApi';
import { QueryTags } from 'lib/rtkQuery/constants';
import { invalidatesTags } from 'lib/rtkQuery/helpers';
import { type ListQueryResponse, type QueryResponse } from 'lib/rtkQuery/types';
import {
  type TListUserDetailQueryResponse,
  type TAvatarResponse,
  type TCreateUserPayload,
  type TCreateUserResponse,
  type TImportUserExcelResponse,
  type TImportUserExcelRow,
  type TUpdateAvatarPayload,
  type TUpdateCurrentUserPasswordPayload,
  type TUpdateCurrentUserPayload,
  type TUpdateUserPayload,
  type TUpdateUserRolePayload,
  type TUpdateUserStatus,
  type TUser,
  type TMeResponse,
  type TUserInfo,
  type TUserListItem,
  type TUserListQueryParams,
  type TCurrentUserAclGroupResponse,
} from 'features/user/types';

const endpoint = '/user';

export const userApi = baseApi
  .enhanceEndpoints({
    addTagTypes: [
      QueryTags.UserAccounts,
      QueryTags.UserDetail,
      QueryTags.DepartmentList,
    ],
  })
  .injectEndpoints({
    endpoints: builder => ({
      // Identity service via the API gateway (`GET /api/user/me`); a missing/deactivated account answers 401.
      GetCurrentUser: builder.query<TUserInfo, void>({
        query: () => ({
          url: `${endpoint}/me`,
          method: 'GET',
        }),
        // An enveloped failure or an empty result must reject: `/me` is the root request, so it may not
        // be swallowed into an empty profile.
        transformResponse: (response: TIdentityResponse<TMeResponse>) => {
          const me = response.results?.[0];
          if (!response.metadata?.success || !me) {
            throw new Error(
              response.metadata?.message ?? 'Failed to load current user'
            );
          }
          return mapMeToCurrentUser(me);
        },
        providesTags: [QueryTags.UserDetail],
      }),

      GetCurrentUserAclGroup: builder.query<TCurrentUserAclGroupResponse, void>(
        {
          query: () => ({
            url: `${endpoint}/current-user/acl-group`,
            method: 'GET',
          }),
        }
      ),

      UpdateCurrentUserAvatar: builder.mutation<
        QueryResponse<TAvatarResponse>,
        TUpdateAvatarPayload
      >({
        query: ({ file }) => {
          const formData = new FormData();
          formData.append('file', file);
          return {
            url: `${endpoint}/current-user/image`,
            method: 'PATCH',
            data: formData,
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          };
        },
        invalidatesTags: invalidatesTags([QueryTags.UserDetail]),
      }),

      GetUserDetailList: builder.query<
        TListUserDetailQueryResponse<TUserListItem[]>,
        void
      >({
        query: () => ({
          url: `${endpoint}/user-details`,
        }),
      }),

      UpdateCurrentUser: builder.mutation<
        QueryResponse<TUserListItem>,
        TUpdateCurrentUserPayload
      >({
        query: (payload: TUpdateCurrentUserPayload) => ({
          url: `${endpoint}/current-user`,
          method: 'PUT',
          data: payload,
        }),
        invalidatesTags: invalidatesTags([QueryTags.UserDetail]),
      }),

      UpdateCurrentUserPassword: builder.mutation<
        QueryResponse<string>,
        TUpdateCurrentUserPasswordPayload
      >({
        query: payload => ({
          url: `${endpoint}/current-user/password`,
          method: 'PATCH',
          data: payload,
        }),
      }),

      GetUserList: builder.query<
        ListQueryResponse<TUser[]>,
        TUserListQueryParams
      >({
        query: params => ({
          url: `${endpoint}`,
          method: 'GET',
          params,
        }),
        providesTags: [QueryTags.UserAccounts],
      }),

      CreateUser: builder.mutation<TCreateUserResponse, TCreateUserPayload>({
        query: payload => ({
          url: `${endpoint}`,
          method: 'POST',
          data: payload,
        }),
        invalidatesTags: invalidatesTags([
          QueryTags.UserAccounts,
          QueryTags.DepartmentList,
        ]),
      }),

      UpdateUser: builder.mutation<QueryResponse<void>, TUpdateUserPayload>({
        query: ({ userCode, userInfo }) => ({
          url: `${endpoint}/${userCode}`,
          method: 'PUT',
          data: userInfo,
        }),
        invalidatesTags: invalidatesTags([QueryTags.UserAccounts]),
      }),

      UpdateUserRole: builder.mutation<
        QueryResponse<void>,
        TUpdateUserRolePayload
      >({
        query: ({ userCode, rolesArr }) => ({
          url: `${endpoint}/${userCode}/roles`,
          method: 'PATCH',
          data: { role_name: rolesArr },
        }),
        invalidatesTags: invalidatesTags([QueryTags.UserAccounts]),
      }),

      UpdateUserStatus: builder.mutation<
        QueryResponse<void>,
        TUpdateUserStatus
      >({
        query: ({ userCode, status }) => ({
          url: `${endpoint}/${userCode}`,
          method: 'PATCH',
          data: { status },
        }),
        invalidatesTags: invalidatesTags([QueryTags.UserAccounts]),
      }),

      // Content-Type is left unset so the browser adds the multipart boundary.
      ImportUserExcel: builder.mutation<TImportUserExcelResponse, File>({
        query: file => {
          const formData = new FormData();
          formData.append('file', file);
          return {
            url: `/import-excel`,
            method: 'POST',
            data: formData,
          };
        },
      }),

      SaveImportedUsers: builder.mutation<void, TImportUserExcelRow[]>({
        query: rows => ({
          url: `/save-data`,
          method: 'POST',
          data: rows,
        }),
        invalidatesTags: invalidatesTags([QueryTags.UserAccounts]),
      }),
    }),
  });

export const {
  useGetCurrentUserQuery,
  useGetCurrentUserAclGroupQuery,
  useUpdateCurrentUserAvatarMutation,
  useGetUserDetailListQuery,
  useUpdateCurrentUserMutation,
  useUpdateCurrentUserPasswordMutation,
  useGetUserListQuery,
  useCreateUserMutation,
  useUpdateUserMutation,
  useUpdateUserStatusMutation,
  useUpdateUserRoleMutation,
  useImportUserExcelMutation,
  useSaveImportedUsersMutation,
} = userApi;
